import {
  ErrorModal,
  type TreeNode,
  TreePickerControlV2,
} from "@dexteel/mesf-core";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { useState } from "react";
import type { FieldError } from "react-hook-form";
import { useStrandLossCodesQuery } from "../../hooks/queries/useStrandLossCatalogQueries";
import type { StrandLossCode } from "../../models/StrandAsset";
import { strandLossesColors } from "../../theme/colors";

const TREE_PICKER_CLEAR_VALUE = 0;

interface StrandLossTreeNode {
  id: number;
  parentId: number | null;
  name: string;
  isLeaf: boolean;
  isActive: boolean;
  children: StrandLossTreeNode[];
}

const buildTreeCodes = (codes: StrandLossCode[]): StrandLossTreeNode | null => {
  const nodeById = new Map<number, StrandLossTreeNode>();
  for (const code of codes) {
    nodeById.set(code.StrandLossCodeId, {
      id: code.StrandLossCodeId,
      parentId: code.ParentStrandLossCodeId,
      name: code.StrandLossCodeName,
      isLeaf: code.IsLeaf,
      isActive: true,
      children: [],
    });
  }

  for (const node of nodeById.values()) {
    if (node.parentId === null) continue;
    nodeById.get(node.parentId)?.children.push(node);
  }

  const roots = [...nodeById.values()].filter((node) => node.parentId === null);
  return roots.length === 1 ? roots[0] : null;
};

interface StrandLossCodeTreePickerProps {
  value: number | null;
  onChange: (value: number | null, categoryId: number | null) => void;
  parentAssetId: number | null;
  fieldError?: FieldError;
}

export const StrandLossCodeTreePicker = ({
  value,
  onChange,
  parentAssetId,
  fieldError,
}: StrandLossCodeTreePickerProps) => {
  const [dismissedError, setDismissedError] = useState("");
  const {
    data: codes = [],
    isLoading,
    error,
  } = useStrandLossCodesQuery(parentAssetId);
  const treeCodes = buildTreeCodes(codes);
  const treeErrorMessage =
    !isLoading && codes.length > 0 && treeCodes === null
      ? "Expected exactly one Strand Loss code-tree root"
      : "";
  const errorMessage =
    error instanceof Error ? error.message : treeErrorMessage;

  if (isLoading || treeCodes === null) {
    return (
      <>
        <TextField
          disabled
          label="Code"
          variant="outlined"
          fullWidth
          size="small"
          slotProps={{
            input: {
              endAdornment: isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null,
            },
          }}
        />
        <ErrorModal
          error={errorMessage === dismissedError ? "" : errorMessage}
          onHide={() => setDismissedError(errorMessage)}
          title="Error loading Strand Loss codes"
        />
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          "& .MuiGrid-root.MuiGrid-container": {
            "& .MuiGrid-item": {
              "& .MuiFormControl-root": {
                "& .MuiInputBase-root": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: `2px solid ${strandLossesColors.treePickerBorder}`,
                  },
                },
              },
            },
          },
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: (theme) => theme.palette.text.primary,
          },
          "& .MuiInputLabel-root.Mui-disabled": {
            color: (theme) => theme.palette.text.secondary,
          },
          "& .MuiInputAdornment-root .MuiSvgIcon-root": {
            color: (theme) => theme.palette.action.active,
          },
        }}
      >
        <FormControl error={!!fieldError} fullWidth>
          <TreePickerControlV2
            inputTitle="Code"
            title="Code"
            dataSource={treeCodes as unknown as TreeNode}
            value={value ?? TREE_PICKER_CLEAR_VALUE}
            onSelect={(selectedId) => {
              const normalizedSelectedId =
                selectedId === TREE_PICKER_CLEAR_VALUE ? null : selectedId;
              const categoryId = codes.find(
                (code) => code.StrandLossCodeId === normalizedSelectedId,
              )?.StrandLossCategoryId;
              onChange(normalizedSelectedId, categoryId ?? null);
            }}
            selectBranch
            allowSearch
            selectActive={false}
            selectInternal={false}
            showClearButton
          />
          {fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
        </FormControl>
      </Box>
      <ErrorModal
        error={errorMessage === dismissedError ? "" : errorMessage}
        onHide={() => setDismissedError(errorMessage)}
        title="Error loading Strand Loss codes"
      />
    </>
  );
};
